import { supabase, handleSupabaseError } from '@/lib/supabase';
import { Transaction, TransactionFilters } from '@/types';
import { TransactionType } from '@/types/enums';

export interface CreateTransactionData {
  nasabahProfileId: string;
  categoryId: string;
  weightKg: number;
  notes?: string;
  evidenceFile?: File;
  /**
   * Tanggal setoran custom (mode fleksibilitas). Hanya berlaku bila cabang
   * mengaktifkan mode fleksibilitas; server menegakkan batas ±1 bulan.
   */
  transactionDate?: Date | null;
}

/** Transaksi + identitas nasabah — untuk daftar transaksi cabang (panel Void). */
export interface BranchTransaction extends Transaction {
  nasabahName: string;
  accountNumber: string;
}

class TransactionService {
  /**
   * Get transactions for a nasabah with optional filters
   */
  async getTransactions(
    nasabahProfileId: string,
    filters?: TransactionFilters
  ): Promise<{ data: Transaction[]; error: string | null }> {
    try {
      let query = supabase
        .from('transactions')
        .select('*')
        .eq('nasabah_profile_id', nasabahProfileId)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate.toISOString());
      }
      if (filters?.type && filters.type !== 'ALL') {
        query = query.eq('type', filters.type);
      }
      if (filters?.minAmount) {
        query = query.gte('amount_locked', filters.minAmount);
      }
      if (filters?.maxAmount) {
        query = query.lte('amount_locked', filters.maxAmount);
      }

      const { data, error } = await query;

      if (error) throw error;

      return {
        data: data.map(this.mapTransaction),
        error: null
      };
    } catch (error) {
      return {
        data: [],
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Get transaction by ID
   */
  async getTransactionById(id: string): Promise<{ data: Transaction | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      return {
        data: this.mapTransaction(data),
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Daftar transaksi terakhir sebuah cabang beserta nama nasabah.
   * Dipakai panel Void Transaksi — termasuk baris yang sudah VOID
   * agar histori selalu terlihat.
   */
  async getBranchTransactions(
    branchId: string,
    limit = 50
  ): Promise<{ data: BranchTransaction[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          *,
          nasabah_profiles(
            account_number,
            users!nasabah_profiles_user_id_fkey(full_name)
          )
        `)
        .eq('branch_id', branchId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return {
        data: (data || []).map((row: any) => ({
          ...this.mapTransaction(row),
          nasabahName: row.nasabah_profiles?.users?.full_name ?? '—',
          accountNumber: row.nasabah_profiles?.account_number ?? '—',
        })),
        error: null
      };
    } catch (error) {
      return {
        data: [],
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Create new deposit transaction
   */
  async createDeposit(
    data: CreateTransactionData,
    createdBy: string
  ): Promise<{ data: Transaction | null; error: string | null }> {
    try {
      // Get current price for category
      const { data: priceData } = await supabase.rpc('get_current_price', {
        p_category_id: data.categoryId
      });

      const currentPrice = priceData || 0;
      const amount = data.weightKg * currentPrice;

      // Get category name
      const { data: categoryData } = await supabase
        .from('waste_categories')
        .select('name, branch_id')
        .eq('id', data.categoryId)
        .single();

      if (!categoryData) throw new Error('Kategori tidak ditemukan');

      // Get nasabah profile for branch_id
      const { data: profileData } = await supabase
        .from('nasabah_profiles')
        .select('branch_id')
        .eq('id', data.nasabahProfileId)
        .single();

      if (!profileData) throw new Error('Profil nasabah tidak ditemukan');

      // Create transaction
      const { data: transactionData, error: transactionError } = await supabase
        .from('transactions')
        .insert({
          branch_id: profileData.branch_id,
          nasabah_profile_id: data.nasabahProfileId,
          type: TransactionType.DEBIT,
          category_id: data.categoryId,
          category_name: categoryData.name,
          weight_kg: data.weightKg,
          unit_price_locked: currentPrice,
          amount_locked: amount,
          notes: data.notes,
          created_by: createdBy,
          ...(data.transactionDate ? { created_at: data.transactionDate.toISOString() } : {})
        })
        .select()
        .single();

      if (transactionError) throw transactionError;

      // Update nasabah balance
      const { error: balanceError } = await supabase.rpc('update_balance', {
        p_nasabah_profile_id: data.nasabahProfileId,
        p_amount: amount
      });

      if (balanceError) throw balanceError;

      // Upload evidence if provided
      if (data.evidenceFile) {
        const evidenceUrl = await this.uploadEvidence(data.evidenceFile, transactionData.id);
        
        await supabase.from('evidences').insert({
          transaction_id: transactionData.id,
          file_url: evidenceUrl,
          file_type: data.evidenceFile.type
        });
      }

      return {
        data: this.mapTransaction(transactionData),
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Upload evidence file to storage
   */
  private async uploadEvidence(file: File, transactionId: string): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${transactionId}-${Date.now()}.${fileExt}`;
    const filePath = `evidences/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('transaction-evidences')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('transaction-evidences')
      .getPublicUrl(filePath);

    return data.publicUrl;
  }

  /**
   * Map database record to Transaction type
   */
  private mapTransaction(data: any): Transaction {
    return {
      id: data.id,
      branchId: data.branch_id,
      nasabahProfileId: data.nasabah_profile_id,
      type: data.type as TransactionType,
      categoryId: data.category_id,
      categoryName: data.category_name,
      weightKg: parseFloat(data.weight_kg),
      unitPriceLocked: parseFloat(data.unit_price_locked),
      amountLocked: parseFloat(data.amount_locked),
      notes: data.notes,
      createdBy: data.created_by,
      createdAt: new Date(data.created_at),
      evidenceUrl: data.evidence_url,
      isVoided: data.is_voided ?? false,
      voidedAt: data.voided_at ? new Date(data.voided_at) : null,
      voidReason: data.void_reason ?? null
    };
  }
}

export const transactionService = new TransactionService();
